import React, { useState, useEffect } from 'react';
import { Ticket, Plus, Search, Copy, CheckCircle, XCircle, Download } from 'lucide-react';
import { adminAPI } from '../../services/api';
import './AdminRedeemCodes.css';

interface RedeemCode {
  id: string;
  code: string;
  name: string;
  plan: string;
  duration_days: number;
  is_redeemed: boolean;
  redeemed_by?: string;
  redeemed_at?: string;
  generated_api_key?: string;
  created_at: string;
  created_by: string;
  expires_at?: string;
  notes?: string;
}

const AdminRedeemCodes: React.FC = () => {
  const [codes, setCodes] = useState<RedeemCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [generatedCodes, setGeneratedCodes] = useState<string[]>([]);
  const [selectedCode, setSelectedCode] = useState<RedeemCode | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    plan: 'pro',
    quantity: 1,
    duration_days: 365
  });

  useEffect(() => {
    fetchCodes();
  }, []);

  const fetchCodes = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.listRedeemCodes();
      console.log('List response:', response.data);
      // Backend returns 'data' array
      setCodes(response.data.data || []);
    } catch (err) {
      console.error('Failed to fetch redeem codes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCodes = async () => {
    try {
      const response = await adminAPI.createRedeemCodes(createForm);
      console.log('Create response:', response.data);
      // Backend returns 'keys' not 'codes'
      const generatedKeys = response.data.keys || response.data.Keys || [];
      const codesList = generatedKeys.map((key: any) => key.code);
      setGeneratedCodes(codesList);
      fetchCodes();
    } catch (err: any) {
      console.error('Create error:', err);
      alert(err.response?.data?.error || 'Failed to create redeem codes');
    }
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    // Show toast notification
  };

  const handleDownloadCodes = () => {
    const csvContent = 'data:text/csv;charset=utf-8,' + 
      'Code,Plan,Created At,Expires At\n' +
      generatedCodes.map(code => `${code},${createForm.plan},${new Date().toISOString()},${new Date(Date.now() + createForm.duration_days * 24 * 60 * 60 * 1000).toISOString()}`).join('\n');
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `redeem-codes-${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredCodes = codes.filter(code =>
    code.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    code.plan.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getPlanColor = (plan: string) => {
    const colors: any = {
      free: 'secondary',
      pro: 'primary',
      enterprise: 'warning'
    };
    return colors[plan.toLowerCase()] || 'secondary';
  };

  return (
    <div className="admin-redeem-codes">
      <div className="page-header">
        <div>
          <h1>Redeem Code Management</h1>
          <p>Generate and manage redeem codes</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
          <Plus size={20} />
          Generate Codes
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3" style={{ marginBottom: '1.5rem' }}>
        <div className="stats-card">
          <div className="stats-card-header">
            <span className="stats-card-title">Total Codes</span>
            <Ticket size={20} style={{ color: 'var(--color-primary)' }} />
          </div>
          <div className="stats-card-value">{codes.length}</div>
        </div>
        <div className="stats-card">
          <div className="stats-card-header">
            <span className="stats-card-title">Active</span>
            <CheckCircle size={20} style={{ color: 'var(--color-success)' }} />
          </div>
          <div className="stats-card-value">
            {codes.filter(c => !c.is_redeemed).length}
          </div>
        </div>
        <div className="stats-card">
          <div className="stats-card-header">
            <span className="stats-card-title">Redeemed</span>
            <XCircle size={20} style={{ color: 'var(--color-warning)' }} />
          </div>
          <div className="stats-card-value">
            {codes.filter(c => c.is_redeemed).length}
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="search-bar">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search redeem codes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Codes Table */}
      <div className="card">
        <div className="card-header">
          <h2>
            <Ticket size={20} />
            All Redeem Codes ({filteredCodes.length})
          </h2>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="loading"></div>
          </div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Plan</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Expires</th>
                  <th>Redeemed By</th>
                  <th>Generated API Key</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCodes.map((code) => (
                  <tr key={code.code} onClick={() => {
                    setSelectedCode(code);
                    setShowDetailModal(true);
                  }} style={{ cursor: 'pointer' }}>
                    <td>
                      <div className="code-cell">
                        <Ticket size={16} />
                        <span className="monospace">{code.code}</span>
                      </div>
                    </td>
                    <td>
                      <span className={`badge badge-${getPlanColor(code.plan)}`}>
                        {code.plan.toUpperCase()}
                      </span>
                    </td>
                    <td>
                      <span className={`badge badge-${!code.is_redeemed ? 'success' : 'secondary'}`}>
                        {!code.is_redeemed ? <CheckCircle size={14} /> : <XCircle size={14} />}
                        {!code.is_redeemed ? 'Active' : 'Redeemed'}
                      </span>
                    </td>
                    <td>
                      <span className="text-secondary">
                        {new Date(code.created_at).toLocaleDateString()}
                      </span>
                    </td>
                    <td>
                      <span className="text-secondary">
                        {code.expires_at ? new Date(code.expires_at).toLocaleDateString() : 'Never'}
                      </span>
                    </td>
                    <td>
                      <span className="text-secondary">
                        {code.redeemed_by || 'N/A'}
                      </span>
                    </td>
                    <td>
                      {code.generated_api_key ? (
                        <div className="code-cell">
                          <span className="monospace" style={{ fontSize: '0.75rem' }}>
                            {code.generated_api_key.substring(0, 20)}...
                          </span>
                          <button 
                            className="btn btn-sm btn-outline"
                            onClick={() => handleCopyCode(code.generated_api_key!)}
                            title="Copy full API key"
                          >
                            <Copy size={14} />
                          </button>
                        </div>
                      ) : (
                        <span className="text-secondary">N/A</span>
                      )}
                    </td>
                    <td>
                      <button 
                        className="btn btn-sm btn-outline"
                        onClick={() => handleCopyCode(code.code)}
                      >
                        <Copy size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Codes Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Generate Redeem Codes</h2>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {generatedCodes.length === 0 ? (
                <>
                  <div className="form-group">
                    <label>Name / Description</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. Black Friday 2024"
                      value={createForm.name}
                      onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label>Plan</label>
                    <select
                      className="form-control"
                      value={createForm.plan}
                      onChange={(e) => setCreateForm({ ...createForm, plan: e.target.value })}
                    >
                      <option value="free">Free</option>
                      <option value="pro">Pro</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Number of Codes</label>
                    <input
                      type="number"
                      className="form-control"
                      min="1"
                      max="100"
                      value={createForm.quantity}
                      onChange={(e) => setCreateForm({ ...createForm, quantity: parseInt(e.target.value) })}
                    />
                  </div>

                  <div className="form-group">
                    <label>Duration (days)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={createForm.duration_days}
                      onChange={(e) => setCreateForm({ ...createForm, duration_days: parseInt(e.target.value) })}
                    />
                  </div>
                </>
              ) : (
                <div className="generated-codes">
                  <div className="alert alert-success">
                    ✅ {generatedCodes.length} codes generated successfully!
                  </div>
                  <div className="codes-list">
                    {generatedCodes.map((code, idx) => (
                      <div key={idx} className="code-item">
                        <span className="monospace">{code}</span>
                        <button 
                          className="btn btn-sm btn-outline"
                          onClick={() => handleCopyCode(code)}
                        >
                          <Copy size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              {generatedCodes.length === 0 ? (
                <>
                  <button className="btn btn-outline" onClick={() => setShowCreateModal(false)}>
                    Cancel
                  </button>
                  <button className="btn btn-primary" onClick={handleCreateCodes}>
                    <Plus size={16} />
                    Generate
                  </button>
                </>
              ) : (
                <>
                  <button className="btn btn-outline" onClick={() => {
                    setGeneratedCodes([]);
                    setShowCreateModal(false);
                  }}>
                    Close
                  </button>
                  <button className="btn btn-primary" onClick={handleDownloadCodes}>
                    <Download size={16} />
                    Download CSV
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedCode && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Redeem Code Details</h2>
              <button className="modal-close" onClick={() => setShowDetailModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Code</label>
                  <div className="code-cell">
                    <span className="monospace">{selectedCode.code}</span>
                    <button 
                      className="btn btn-sm btn-outline"
                      onClick={() => handleCopyCode(selectedCode.code)}
                    >
                      <Copy size={14} />
                    </button>
                  </div>
                </div>
                
                <div className="detail-item">
                  <label>Plan</label>
                  <span className={`badge badge-${getPlanColor(selectedCode.plan)}`}>
                    {selectedCode.plan.toUpperCase()}
                  </span>
                </div>

                <div className="detail-item">
                  <label>Status</label>
                  <span className={`badge badge-${!selectedCode.is_redeemed ? 'success' : 'secondary'}`}>
                    {!selectedCode.is_redeemed ? 'Active' : 'Redeemed'}
                  </span>
                </div>

                <div className="detail-item">
                  <label>Duration</label>
                  <span>{selectedCode.duration_days} days</span>
                </div>

                <div className="detail-item">
                  <label>Created At</label>
                  <span>{new Date(selectedCode.created_at).toLocaleString()}</span>
                </div>

                {selectedCode.expires_at && (
                  <div className="detail-item">
                    <label>Expires At</label>
                    <span>{new Date(selectedCode.expires_at).toLocaleString()}</span>
                  </div>
                )}

                {selectedCode.is_redeemed && (
                  <>
                    <div className="detail-item">
                      <label>Redeemed By</label>
                      <span>{selectedCode.redeemed_by || 'Unknown'}</span>
                    </div>

                    <div className="detail-item">
                      <label>Redeemed At</label>
                      <span>{selectedCode.redeemed_at ? new Date(selectedCode.redeemed_at).toLocaleString() : 'N/A'}</span>
                    </div>

                    {selectedCode.generated_api_key && (
                      <div className="detail-item" style={{ gridColumn: '1 / -1' }}>
                        <label>Generated API Key</label>
                        <div className="code-cell">
                          <span className="monospace" style={{ fontSize: '0.85rem', wordBreak: 'break-all' }}>
                            {selectedCode.generated_api_key}
                          </span>
                          <button 
                            className="btn btn-sm btn-primary"
                            onClick={() => handleCopyCode(selectedCode.generated_api_key!)}
                          >
                            <Copy size={14} />
                            Copy
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {selectedCode.notes && (
                  <div className="detail-item" style={{ gridColumn: '1 / -1' }}>
                    <label>Notes</label>
                    <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>{selectedCode.notes}</p>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowDetailModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRedeemCodes;